import { CgSpinner } from 'react-icons/cg';
import { MdOutlineErrorOutline } from 'react-icons/md';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';

interface FileStatusBadgeProps {
  status: string;
  onViewError?: () => void;
  /**
   * ATAD-146: the File's kind. A model import is a single server-side step,
   * not the ID-file pipeline's four, so its statuses read differently.
   */
  kind?: string | null;
  /**
   * ATAD-146: a file imported from an Alliant data source (File.dataSource
   * set). Its import is one server-side job, so its statuses read like a
   * model's whatever its kind.
   */
  imported?: boolean;
}

// Status presentation table. Keep in sync with the enum in
// server/db/models/file.model.js. Kind drives the icon + color treatment;
// label is the user-facing text.
const STATUS_PRESENTATION: Record<
  string,
  { kind: 'progress' | 'success' | 'error'; label: string }
> = {
  uploading: { kind: 'progress', label: 'Uploading' },
  pending: { kind: 'progress', label: 'Pending' },
  processing: { kind: 'progress', label: 'Processing (Step 1 of 4)' },
  matching: { kind: 'progress', label: 'Matching (Step 2 of 4)' },
  matched: { kind: 'progress', label: 'Indexing (Step 3 of 4)' },
  completed: { kind: 'success', label: 'Completed' },
  'uploading-failed': { kind: 'error', label: 'Upload failed' },
  'processing-failed': { kind: 'error', label: 'Processing failed' },
  'matching-failed': { kind: 'error', label: 'Matching failed' },
  failed: { kind: 'error', label: 'Failed' },
};

// ATAD-146: labels for model files (kind 'model') and data-source imports.
// Icon and color still come from STATUS_PRESENTATION. "Processing (Step 1
// of 4)" would promise steps that never come. Only ID imports match.
const IMPORT_STATUS_LABELS: Record<string, string> = {
  pending: 'Queued',
  processing: 'Importing…',
  matching: 'Matching…',
  completed: 'Imported',
  'processing-failed': 'Import failed',
};

/**
 * A small status pill for File rows: icon + colored label, plus an optional
 * "View error" button when the file is in a failed state. Mirrors the
 * DistributionStatus / DistributionsTable visual language used elsewhere
 * in the platform so the look is consistent.
 */
export default function FileStatusBadge({
  status,
  onViewError,
  kind,
  imported = false,
}: FileStatusBadgeProps) {
  const base =
    STATUS_PRESENTATION[status] ?? {
      kind: 'progress',
      label: status?.replace('-', ' ') ?? 'Unknown',
    };
  const presentation =
    (kind === 'model' || imported) && IMPORT_STATUS_LABELS[status]
      ? { ...base, label: IMPORT_STATUS_LABELS[status] }
      : base;

  if (presentation.kind === 'progress') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-blue-light">
        <CgSpinner className="text-base animate-spin" aria-hidden="true" />
        <span>{presentation.label}</span>
      </span>
    );
  }

  if (presentation.kind === 'success') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-green-light">
        <IoIosCheckmarkCircleOutline
          className="text-base"
          aria-hidden="true"
        />
        <span>{presentation.label}</span>
      </span>
    );
  }

  // Error: badge + an optional "View error" affordance.
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span className="inline-flex items-center gap-1.5 text-orange">
        <MdOutlineErrorOutline className="text-base" aria-hidden="true" />
        <span>{presentation.label}</span>
      </span>
      {onViewError && (
        <button
          type="button"
          onClick={onViewError}
          className="text-xs text-blue hover:text-blue-light hover:underline font-medium"
        >
          View error
        </button>
      )}
    </span>
  );
}
